import { OrganizationModels } from '@models/organization.model';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { OrganizationUsersActions } from '@states/organization-users/organization-users.action-types';
import _ from 'lodash';
import { OrganizationsActions } from '@states/organization/organization.actions-types';
import { PermissionGroupModels, SystemSecurityGroupModels } from '@models/group.model';
import { AppUser } from '../../../user/user.model';
import User = AppUser.User;
import { Dictionary } from '@ngrx/entity/src/models';
import { removeInvitedOrg, removeInvitedOrgFail } from '@states/organization-users/organization-users.actions';

export declare interface OrganizationUsersState extends EntityState<User> {
  isLoading: boolean;
  isInitialLoaded: boolean;
  permissionGroups: Dictionary<PermissionGroupModels.PermissionGroupDocumentBase>;
  systemPermissionGroups: SystemSecurityGroupModels.SystemSecurityGroupDocument[];
  invitedOrganizations: PermissionGroupModels.Organization[];
  removingIds: Dictionary<boolean>;
}

export const adapter: EntityAdapter<User> = createEntityAdapter<User>({
  selectId: (organizationUser: User) => organizationUser._id,
});

export const initialState: OrganizationUsersState = adapter.getInitialState({
  isLoading: false,
  isInitialLoaded: false,
  permissionGroups: {},
  systemPermissionGroups: [],
  invitedOrganizations: [],
  removingIds: {},
});

export const organizationUsersStateReducer = createReducer(
  initialState,
  on(OrganizationUsersActions.resetToInitialState, () => {
    return {
      ...initialState,
    };
  }),
  on(OrganizationUsersActions.getOrganizationUsersSuccess, (state, { organizationUsers }) => {
    return adapter.addMany(organizationUsers, {
      ...state,
      isInitialLoaded: true,
    });
  }),
  on(OrganizationUsersActions.getInvitedOrganizationsSuccess, (state, { invitedOrganizations }) => {
    return {
      ...state,
      invitedOrganizations,
    };
  }),
  on(OrganizationUsersActions.setIsLoading, (state, { isLoading }) => {
    return {
      ...state,
      isLoading,
    };
  }),
  on(OrganizationUsersActions.resetEntities, (state) => {
    return adapter.removeAll({
      ...state,
    });
  }),
  on(OrganizationsActions.removeUserSuccess, (state, { userId }) => {
    return adapter.removeOne(userId, { ...state });
  }),
  on(OrganizationUsersActions.getOrganizationPermissionGroupsSuccess, (state, { permissionGroups }) => {
    return {
      ...state,
      permissionGroups,
    };
  }),
  on(OrganizationUsersActions.getSystemPermissionGroupsSuccess, (state, { permissionGroups }) => {
    return {
      ...state,
      systemPermissionGroups: permissionGroups,
    };
  }),
  on(OrganizationUsersActions.updateOrganizationUserPermissionsLocally, (state, { userId, groupIds }) => {
    return {
      ...state,
      permissionGroups: updateOrganizationUserPermissionsLocally(userId, groupIds, state.permissionGroups),
    };
  }),
  on(OrganizationUsersActions.setOrganizationInvitedOrgPermissions, (state, { toUpdate }) => {
    return {
      ...state,
      permissionGroups: toUpdate,
    };
  }),
  on(OrganizationUsersActions.removeInvitedOrgSuccess, (state, { orgId }) => {
    const invitedOrgs = state.invitedOrganizations.filter(org => org._id !== orgId);
    const removingIds = { ...state.removingIds };
    delete removingIds[orgId];
    return {
      ...state,
      invitedOrganizations: invitedOrgs,
      removingIds,
    };
  }),
  on(OrganizationUsersActions.removeInvitedOrgFail, (state, { orgId }) => {
    const removingIds = { ...state.removingIds };
    delete removingIds[orgId];
    return {
      ...state,
      removingIds,
    };
  }),
  on(OrganizationUsersActions.setInvitedOrganization, (state, { invitedOrganization }) => {
    const invitedOrgs = [...state.invitedOrganizations];
    invitedOrgs.push(invitedOrganization);
    return {
      ...state,
      invitedOrganizations: invitedOrgs,
    };
  }),
  on(OrganizationUsersActions.setOrganizationPermissionGroups, (state, { permissionGroups }) => {
    return {
      ...state,
      permissionGroups: {
        ...state.permissionGroups,
        ...permissionGroups,
      },
    };
  }),
  on(OrganizationUsersActions.removeInvitedOrg, (state, { orgId }) => {
    const removingIds = { ...state.removingIds };
    removingIds[orgId] = true;
    return {
      ...state,
      removingIds,
    };
  }),
);

const updateOrganizationUserPermissionsLocally = (userId: string, groupIds: string[], permissionGroups: Dictionary<PermissionGroupModels.PermissionGroupDocumentBase>): Dictionary<PermissionGroupModels.PermissionGroupDocumentBase> => {
  const permissionGroupsArray = Object.keys(permissionGroups);
  const result: Dictionary<PermissionGroupModels.PermissionGroupDocumentBase> = {};
  permissionGroupsArray.forEach(groupId => {
    const group = permissionGroups[groupId];
    if (groupIds.includes(groupId)) {
      result[groupId] = {
        ...group,
        users: {
          ...group.users,
          [userId]: {},
        },
      };
    } else {
      const groupCopy = _.cloneDeep(group);
      delete groupCopy.users[userId];
      result[groupId] = groupCopy;
    }
  });

  return result;
};
